import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.ts';
import { Betolto, HibaUzenet, Valaszto } from '../ui/elemek.tsx';
import type { IrodaUrlapAdat } from './irodaUrlap.ts';

interface Vezeto { user_id: string; profil: { nev: string } | null }

/** Az új lead kiosztási módja (super admin): körforgás, vagy minden lead a kijelölt irodavezetőhöz. Csak meglévő irodánál. */
export default function KiosztasBlokk({ officeId, mod, vezetoId, onMod, onVezeto, hiba }: {
  officeId: string; mod: IrodaUrlapAdat['kiosztasMod']; vezetoId: string | null;
  onMod: (m: IrodaUrlapAdat['kiosztasMod']) => void; onVezeto: (id: string | null) => void; hiba?: string;
}) {
  const vezetok = useQuery({
    queryKey: ['super-iroda-vezetok', officeId],
    queryFn: async () => {
      const { data, error } = await supabase.from('office_members')
        .select('user_id, profil:profiles(nev)').eq('office_id', officeId).eq('szerep', 'vezeto').eq('aktiv', true);
      if (error) throw error;
      return (data as unknown as Vezeto[]).sort((a, b) => (a.profil?.nev ?? '').localeCompare(b.profil?.nev ?? '', 'hu'));
    },
  });
  // Belépett-e a vezető (van TOTP-ja): csak a kész tag kaphat leadet.
  const keszTagok = useQuery({
    queryKey: ['super-iroda-kesz', officeId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('kollegak_kesz', { p_office_id: officeId });
      if (error) throw error;
      return new Map<string, boolean>(data.map((m) => [m.user_id, m.kesz]));
    },
  });
  const nemKeszVezeto = mod === 'vezetohoz' && vezetoId !== null && keszTagok.data?.get(vezetoId) === false;

  return (
    <section className="grid gap-4 px-5 py-5" data-testid="kiosztas-blokk">
      <h2 className="text-lg font-medium tracking-[-0.015em]">Kiosztás</h2>
      <fieldset className="grid gap-1">
        <legend className="sr-only">Az új lead kiosztása</legend>
        <label className="flex min-h-11 items-center gap-3">
          <input type="radio" name="kiosztas-mod" className="size-5 accent-kiemelt" data-testid="kiosztas-mod-automatikus"
            checked={mod === 'automatikus'} onChange={() => onMod('automatikus')} />
          Körforgás az értékesítők között (alapértelmezés)
        </label>
        <label className="flex min-h-11 items-center gap-3">
          <input type="radio" name="kiosztas-mod" className="size-5 accent-kiemelt" data-testid="kiosztas-mod-vezetohoz"
            checked={mod === 'vezetohoz'} onChange={() => onMod('vezetohoz')} />
          Minden új lead a kijelölt irodavezetőhöz
        </label>
      </fieldset>
      {mod === 'vezetohoz' && (
        <>
          {vezetok.isPending && <Betolto />}
          <HibaUzenet hiba={vezetok.error ?? keszTagok.error} />
          {vezetok.data && (
            <Valaszto label="Irodavezető" data-testid="kiosztas-vezeto" value={vezetoId ?? ''} hiba={hiba}
              onChange={(e) => onVezeto(e.target.value === '' ? null : e.target.value)}>
              <option value="">Válassz irodavezetőt…</option>
              {vezetok.data.map((v) => (
                <option key={v.user_id} value={v.user_id}>
                  {v.profil?.nev ?? 'Névtelen'}{keszTagok.data?.get(v.user_id) === false ? ' (még nem lépett be)' : ''}
                </option>
              ))}
            </Valaszto>
          )}
          {vezetok.data?.length === 0 && <p className="text-sm text-szoveg-halk">Az irodának nincs aktív vezetője; előbb vegyél fel egyet a Kollégák oldalon.</p>}
          {nemKeszVezeto && (
            <p role="status" data-testid="kiosztas-figyelmeztetes" className="text-sm text-szoveg-halk">
              Amíg a kijelölt vezető nem lép be (és nem állítja be a kétlépcsős azonosítást), a lead a másik kész vezetőhöz kerül.
            </p>
          )}
        </>
      )}
    </section>
  );
}
